import { db, FORMENTWURF_HALTBAR_MS, type Formentwurf } from './localDb.js'

/**
 * Die örtliche Notfallkopie eines Erfassungsformulars.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Die Rangfolge: Server zuerst, Browser als Netz darunter
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Gespeichert wird in erster Linie auf den Server – dort ist der Entwurf von
 * jedem Gerät aus erreichbar, dort gilt die Mandantentrennung der Datenbank,
 * und dort liegt er auch noch, wenn das Telefon in den Schacht fällt.
 *
 * Diese Kopie greift in den Augenblicken, in denen der Server nicht erreichbar
 * ist: Keller ohne Empfang, Tunnel, Akku leer, Browser abgestürzt. Sie wird
 * bei jeder Änderung mitgeschrieben und verschwindet, sobald der Serverstand
 * nachweislich derselbe ist.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Mandantentrennung
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Jeder Eintrag trägt Mandant und Person, und jede Abfrage filtert auf beide.
 * Eine mandantenübergreifende Wiederherstellung gibt es damit nicht: `hole()`
 * gibt einen fremden Eintrag nicht heraus, auch nicht unter derselben
 * Kennung.
 *
 * Drei Dinge begrenzen, was liegen bleibt:
 *
 *   **Inhalt.** Nur der Stand der Maske, den der Benutzer ohnehin vor sich
 *   hat. Keine Zugangsdaten, keine Sitzungskennung, keine Kundenliste, keine
 *   Preise aus dem Katalog – und nicht mehr als `HOECHSTENS_ZEICHEN`.
 *
 *   **Zeit.** Vierundzwanzig Stunden, geprüft beim Aufräumen UND beim Lesen.
 *
 *   **Sitzung.** Beim Abmelden wird die Tabelle geleert, beim Wechsel von
 *   Betrieb oder Person alles entfernt, was nicht dem neuen Paar gehört
 *   (`clearOnLogout` und `bindeAn` in `localDb.ts`). Wer zurückwechselt,
 *   findet seinen eigenen angefangenen Beleg wieder, solange die Frist läuft.
 *
 * Das ist bewusst strenger als beim Ausgangskorb, der Abmelden überlebt. Der
 * Unterschied liegt im Inhalt: Im Korb liegt fertige, abgeschickte Arbeit;
 * hier liegt eine Notfallkopie, deren eigentlicher Ort der Server ist.
 */

/**
 * Wie lange eine Kopie gilt.
 *
 * Vierundzwanzig Stunden, und das ist eine Verkürzung: Zuerst standen hier
 * vierzehn Tage, gedacht als Grosszügigkeit. Sie war keine. Der Zweck dieser
 * Kopie ist, einen Arbeitsgang zu retten – leerer Akku, abgestürzter Browser,
 * Tunnel. Das entscheidet sich in Minuten bis Stunden, nicht in Wochen.
 *
 * Die Frist wird an DREI Stellen durchgesetzt: beim Aufräumen, beim Lesen und
 * beim Abmelden. Nur aufzuräumen genügt nicht – wer ein Gerät zwei Wochen
 * nicht anfasst, bekäme beim nächsten Öffnen einen abgelaufenen Entwurf
 * angeboten, weil das Aufräumen erst in demselben Augenblick läuft.
 *
 * Der Wert steht in `localDb.ts`, damit das Abmelden dieselbe Zahl benutzt.
 */
const HALTBAR_MS = FORMENTWURF_HALTBAR_MS

/**
 * Wie viel eine Kopie höchstens umfassen darf.
 *
 * Ein Beleg mit dreissig Positionen liegt bei wenigen Kilobyte. Hundert
 * Kilobyte sind also keine Grenze für normale Arbeit, sondern ein Riegel
 * gegen den Unfall: Gerät irgendwann ein Bild, eine Liste oder ein ganzer
 * Serverzustand in den Formularzustand, soll er nicht stillschweigend in der
 * Browserablage landen. Dann wird nichts gespeichert, und der Server bleibt
 * der einzige Weg – was er ohnehin sein soll.
 */
const HOECHSTENS_ZEICHEN = 100_000

function istVerfuegbar(): boolean {
  return typeof indexedDB !== 'undefined'
}

export interface Ablagekennung {
  tenantId: string
  userId: string
}

/** Die Kennung eines noch nicht angelegten Belegs. */
export function neuKennung(art: 'rechnung' | 'offerte'): string {
  return `neu:${art}`
}

/** Schreiben – fehlertolerant. Eine fehlende Ablage darf nie die Maske stören. */
export async function merke(
  id: string,
  wer: Ablagekennung,
  art: 'rechnung' | 'offerte',
  stand: unknown,
  serverVersion: number | null
): Promise<void> {
  if (!istVerfuegbar()) return
  try {
    /*
      Der Riegel gegen zu viel.

      Gemessen wird am serialisierten Stand – derselbe Wert, der auch
      abgelegt wird. Ist er zu gross, bleibt die alte Kopie liegen statt eine
      halbe neue zu schreiben: Eine veraltete, vollständige Kopie ist mehr
      wert als eine aktuelle, abgeschnittene.
    */
    if (JSON.stringify(stand ?? null).length > HOECHSTENS_ZEICHEN) return
    await db.formentwuerfe.put({
      id,
      tenantId: wer.tenantId,
      userId: wer.userId,
      art,
      stand,
      gespeichertAm: Date.now(),
      serverVersion
    })
  } catch {
    /* Kein Platz, privater Modus, gesperrte Ablage: dann eben nicht. */
  }
}

/** Lesen – nur was dieser Person in diesem Betrieb gehört. */
export async function hole(id: string, wer: Ablagekennung): Promise<Formentwurf | null> {
  if (!istVerfuegbar()) return null
  try {
    const e = await db.formentwuerfe.get(id)
    if (!e) return null
    if (e.tenantId !== wer.tenantId || e.userId !== wer.userId) return null
    /*
      Abgelaufen heisst: wird nicht mehr angeboten, und verschwindet gleich.

      Die Prüfung steht hier und nicht nur im Aufräumen, weil das Aufräumen
      beim Öffnen derselben Maske läuft – ein Entwurf von vorletzter Woche
      wäre sonst schon angeboten, bevor er entfernt wird.
    */
    if (Date.now() - e.gespeichertAm > HALTBAR_MS) {
      await db.formentwuerfe.delete(id)
      return null
    }
    return e
  } catch {
    return null
  }
}

/**
 * Entfernen.
 *
 * Wird aufgerufen, sobald der Serverstand übernommen ist – und beim
 * ausdrücklichen Verwerfen durch den Benutzer. Zweimal dieselbe
 * Wiederherstellung anzubieten erzeugt doppelte Entwürfe; genau das soll
 * nicht passieren.
 */
export async function vergiss(id: string): Promise<void> {
  if (!istVerfuegbar()) return
  try {
    await db.formentwuerfe.delete(id)
  } catch {
    /* siehe oben */
  }
}

/**
 * Nach dem Anlegen eines Belegs die vorläufige Kennung umhängen.
 *
 * Solange kein Beleg existiert, liegt die Kopie unter `neu:rechnung`. Sobald
 * der Server eine Kennung vergibt, zieht sie um – sonst bliebe eine Kopie
 * liegen, die beim nächsten neuen Beleg als Wiederherstellung angeboten wird,
 * obwohl ihr Inhalt längst gespeichert ist.
 */
export async function benenneUm(alt: string, neu: string): Promise<void> {
  if (!istVerfuegbar()) return
  try {
    const e = await db.formentwuerfe.get(alt)
    if (!e) return
    await db.formentwuerfe.put({ ...e, id: neu })
    await db.formentwuerfe.delete(alt)
  } catch {
    /* siehe oben */
  }
}

/** Alte, nie abgeholte Kopien aufräumen. */
export async function raeumeAuf(): Promise<void> {
  if (!istVerfuegbar()) return
  try {
    const grenze = Date.now() - HALTBAR_MS
    await db.formentwuerfe.where('gespeichertAm').below(grenze).delete()
  } catch {
    /* siehe oben */
  }
}
